import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

const ALL_ROW = { routeCd: "ALL", routeNm: "ALL" };

/**
 * 자원(Resource) 기준 공정(Route) 선택 전용 팝업 — old `ResourceRoutePopup.jsx` 대응.
 * `RoutePopup.js`(전체 공정 무필터 조회)와는 별개 컴포넌트 — 특정 resourceCd가 실제로 쓰이는
 * BOR(공정-자원) 조합만 조회하고(`factoryplan/bors`, `resourceCd` 정확일치 필터), 맨 앞에 old와
 * 동일한 "ALL"(전체 허용) 와일드카드 행을 추가한다. 더블클릭 선택, 팝업 자체는 target 개념을 몰라도
 * 되고(호출측이 어느 필드에 꽂을지 결정) routeCd/routeNm만 돌려준다.
 *
 * props: open, onClose(), planScope, resourceCd, onSelect({routeCd, routeNm})
 */
const ResourceRoutePopup = ({ t, open, onClose, planScope, resourceCd, onSelect }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_ROUTE_SELECT"), viewName: "" }), [t]);

  const columnDefs = useMemo(() => ([
    { headerName: t("FP_ROUTE_CD"), field: "routeCd", colId: "routeCd", width: 180, editable: false },
    { headerName: t("FP_ROUTE_NM"), field: "routeNm", colId: "routeNm", width: 220, editable: false },
    { headerName: t("FP_DESC_TXT"), field: "descTxt", colId: "descTxt", width: 150, editable: false },
    {
      headerName: t("FP_COL_AUDIT"), groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: t("updatedTs"), field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: t("updatedBy"), field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        { headerName: t("createdTs"), field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: t("createdBy"), field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
      ]
    }
  ]), [t]);

  const loadRoutes = () => {
    if (!planScope || !resourceCd) return;
    fpCommonService.getBorRoutes({ planScope, resourceCd })
      .then(response => setRowData([ALL_ROW, ...(isEmpty(response.data) ? [] : response.data)]))
      .catch(e => { debug(e); setRowData([ALL_ROW]); });
  };

  useEffect(() => {
    if (!open || !planScope || !resourceCd) return;
    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope, resourceCd]);

  const onRowDoubleClicked = (e) => {
    if (!e.data) return;
    onSelect({ routeCd: e.data.routeCd, routeNm: e.data.routeNm });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("FP_ROUTE_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(ResourceRoutePopup);
